import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Editor } from '@tiptap/react';
import { useAction } from '@cotera/client/v0/actions/framework';
import { useOpenToolModal } from '@cotera/client/v0/components/tool-connections';
import { Icon, Tooltip } from '@cotera/client/app/components/ui';
import { VoiceRecordingButton } from '@cotera/client/v0/components/voice-recording-button';
import { Toggle } from '@cotera/client/v0/components/ui/toggle';
import { useCocoMentions } from '@cotera/client/app/hooks/use-coco-mentions';
import { useNavigateOnResourceMentionClick } from '@cotera/client/app/hooks/use-resource-mention-navigation';
import type { InitialAction, DG } from '@cotera/sdk/core';
import { NewChatAction } from '../actions/new-chat.action';
import {
  peekChatDraft,
  setChatDraft,
  removeChatDraft,
  NEW_CHAT_DRAFT_ID,
} from '../models/chat-draft';
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputSubmit,
  PromptInputTools,
  PromptInputActionMenu,
  PromptInputActionMenuTrigger,
  PromptInputActionMenuContent,
  PromptInputActionAddAttachments,
  PromptInputAttachments,
  PromptInputAttachment,
  COMPOSER_EDITOR_MAX_HEIGHT,
} from '@cotera/client/v0/components/ai-elements/prompt-input';
import type { PromptInputMessage } from '@cotera/client/v0/components/ai-elements/prompt-input';
import { ChatTipTapInput } from '@cotera/client/v0/components/ai-elements/chat-tiptap-input';
import { trimEmptyEdgeBlocks } from '@cotera/client/app/components/app/rte/lib/trim-empty-blocks';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import { cn } from '@cotera/client/v0/lib/utils';
import { useAnalytics, CocoEvent } from '@cotera/client/app/etc';

export type NewChatPromptInputHandle = {
  setValue: (text: string) => void;
};

export type NewChatPromptInputProps = {
  placeholder?: string;
  className?: string;
  /** When true, disables the input (e.g. when agent is locked) */
  disabled?: boolean;
  /** Model to use for the chat (overrides default) */
  model?: string;
  /** When set, the new chat is assigned to this folder on first message. */
  folderId?: string;
};

export const NewChatPromptInput = React.forwardRef<
  NewChatPromptInputHandle,
  NewChatPromptInputProps
>(
  (
    {
      placeholder = 'What do you want to achieve?',
      className = 'divide-y-0 pb-4 [&_[data-slot=input-group]]:rounded-3xl',
      disabled = false,
      model,
      folderId,
    },
    ref
  ) => {
    const newChatAction = useAction(NewChatAction);
    const analytics = useAnalytics();

    // Restore a draft persisted before a reload (e.g. the auto-reload on deploy).
    // The new-chat composer has no conversationId yet, so it uses a stable
    // sentinel key. The draft is read once for the editor's initial content.
    const initialDraft = useMemo(() => peekChatDraft(NEW_CHAT_DRAFT_ID), []);
    const initialEditorValue = useMemo<
      DG.DocumentEditor.DocumentState | undefined
    >(() => {
      if (initialDraft === null) {
        return undefined;
      }
      if (initialDraft.editorState !== undefined) {
        return initialDraft.editorState as DG.DocumentEditor.DocumentState;
      }
      const text = initialDraft.text ?? '';
      if (text === '') {
        return undefined;
      }
      return {
        type: 'doc',
        content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
      };
    }, [initialDraft]);

    const [inputValue, setInputValue] = useState(
      () => initialDraft?.text ?? ''
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [autoApproveTools, setAutoApproveTools] = useState(false);
    const [planMode, setPlanMode] = useState(false);
    const tiptapRef = useRef<{
      setValue: (text: string) => void;
      getEditor: () => Editor | null;
      setPlaceholder: (placeholder: string) => void;
    }>(null);
    useImperativeHandle(ref, () => ({
      setValue: (text: string) => {
        tiptapRef.current?.setValue(text);
        setInputValue(text);
      },
      setPlaceholder: (placeholder: string) => {
        tiptapRef.current?.setPlaceholder(placeholder);
      },
      getEditor: () => tiptapRef.current?.getEditor() ?? null,
    }));

    const {
      options: mentionOptions,
      groups: mentionGroups,
      isLoading: mentionLoading,
    } = useCocoMentions();
    const openToolModal = useOpenToolModal();
    const navigateResourceMention = useNavigateOnResourceMentionClick();

    // Persist the in-progress draft (debounced) so it survives a reload.
    const draftSaveTimer = useRef<number | null>(null);
    const cancelPendingDraftSave = useCallback(() => {
      if (draftSaveTimer.current !== null) {
        window.clearTimeout(draftSaveTimer.current);
        draftSaveTimer.current = null;
      }
    }, []);
    const saveDraft = useCallback(
      (text: string, editorState?: { type: 'doc'; content?: unknown[] }) => {
        cancelPendingDraftSave();
        draftSaveTimer.current = window.setTimeout(() => {
          setChatDraft(NEW_CHAT_DRAFT_ID, { text, editorState });
        }, 400);
      },
      [cancelPendingDraftSave]
    );
    useEffect(() => cancelPendingDraftSave, [cancelPendingDraftSave]);

    const handleMentionClick = useCallback(
      (data: MentionClickData) => {
        if (navigateResourceMention(data)) {
          return;
        }
        const toolKey = data.toolKey ?? data.name;
        if (toolKey !== undefined && toolKey !== '') {
          openToolModal(toolKey);
        }
      },
      [navigateResourceMention, openToolModal]
    );

    const handleSubmit = useCallback(
      (
        message: PromptInputMessage,
        _event: React.FormEvent<HTMLFormElement>
      ) => {
        const hasContent =
          (message.type === 'text'
            ? (message.text?.trim().length ?? 0) > 0
            : (message.editorState?.content?.length ?? 0) > 0) ||
          (message.files?.length ?? 0) > 0;
        if (!hasContent || isSubmitting) {
          return;
        }

        setIsSubmitting(true);

        // Sending creates the chat and navigates away — drop the draft (and any
        // pending debounced write) so the first message can't reappear on the
        // converted chat page or in the next new-chat composer.
        cancelPendingDraftSave();
        removeChatDraft(NEW_CHAT_DRAFT_ID);

        const initialActions: InitialAction[] | undefined = autoApproveTools
          ? [{ type: 'auto_approve' }]
          : undefined;
        const baseParams = {
          autoApproveTools,
          planMode,
          ...(initialActions !== undefined && { initialActions }),
          model,
          ...(folderId !== undefined && { folderId }),
        };
        const submission =
          message.type === 'editor-state'
            ? newChatAction.execute({
                editorState: message.editorState,
                files: message.files,
                ...baseParams,
              })
            : newChatAction.execute({
                text: message.text?.trim(),
                files: message.files,
                ...baseParams,
              });
        // On success the page navigates away and this component unmounts. Only a
        // failure — e.g. a tabular attachment that could not be uploaded — lands
        // back here, so re-enable the composer instead of leaving it stuck on
        // the submit spinner.
        void submission.then((result) => {
          if (result.isErr()) {
            setIsSubmitting(false);
          }
        });
      },
      [
        isSubmitting,
        newChatAction,
        autoApproveTools,
        planMode,
        model,
        folderId,
        cancelPendingDraftSave,
      ]
    );

    const getMessageContent = useCallback(
      (files: import('ai').FileUIPart[]) => {
        const editor = tiptapRef.current?.getEditor();
        if (editor !== undefined && editor !== null) {
          const json = editor.getJSON();
          const text = editor.getText().trim();
          if (text === '' && files.length === 0) {
            return {
              message: { type: 'text' as const, text: '', files },
              clear: () => {},
            };
          }
          return {
            message: {
              type: 'editor-state' as const,
              editorState: trimEmptyEdgeBlocks(json) as {
                type: 'doc';
                content?: unknown[];
              },
              files,
            },
            clear: () => {
              editor.commands.clearContent();
              setInputValue('');
            },
          };
        }
        return {
          message: { type: 'text' as const, text: inputValue, files },
        };
      },
      [inputValue]
    );

    const handleTranscriptionComplete = useCallback(
      (text: string) => {
        const current = tiptapRef.current?.getEditor()?.getText() ?? inputValue;
        const newValue = current ? `${current} ${text}` : text;
        tiptapRef.current?.setValue(newValue);
        setInputValue(newValue);
      },
      [inputValue]
    );

    const hasInput = inputValue.trim().length > 0;

    return (
      <div
        className={cn(disabled && 'pointer-events-none opacity-60')}
        aria-disabled={disabled}
      >
        <PromptInput
          globalDrop
          onSubmit={handleSubmit}
          onFilesAdded={(count) =>
            analytics.track(CocoEvent.ChatFileAttached, {
              // The new-chat landing has no conversation yet — always scratch.
              surface: 'scratch',
              fileCount: count,
              source: 'new_chat',
            })
          }
          getMessageContent={getMessageContent}
          className={className}
        >
          <PromptInputHeader>
            <PromptInputAttachments>
              {(attachment) => <PromptInputAttachment data={attachment} />}
            </PromptInputAttachments>
          </PromptInputHeader>
          <PromptInputBody>
            <ChatTipTapInput
              ref={tiptapRef}
              placeholder={placeholder}
              className={cn(
                COMPOSER_EDITOR_MAX_HEIGHT,
                'min-h-16 overflow-y-auto px-5 md:text-base placeholder:text-slate-400'
              )}
              value={initialEditorValue}
              onTextChange={setInputValue}
              onChange={(editor) =>
                saveDraft(
                  editor.getText(),
                  editor.getJSON() as { type: 'doc'; content?: unknown[] }
                )
              }
              mentionOptions={mentionOptions}
              mentionGroups={mentionGroups}
              mentionLoading={mentionLoading}
              onMentionClick={handleMentionClick}
            />
          </PromptInputBody>
          <PromptInputFooter className="p-2.5">
            <PromptInputTools>
              <PromptInputActionMenu>
                <PromptInputActionMenuTrigger />
                <PromptInputActionMenuContent>
                  <PromptInputActionAddAttachments />
                </PromptInputActionMenuContent>
              </PromptInputActionMenu>
              {hasInput && (
                <VoiceRecordingButton
                  onTranscriptionComplete={handleTranscriptionComplete}
                />
              )}
              <Tooltip tooltipContent="Auto-approve all tools" side="top">
                <Toggle
                  pressed={autoApproveTools}
                  onPressedChange={(pressed) => setAutoApproveTools(pressed)}
                  size="sm"
                  variant="ghost"
                  aria-label="Toggle auto-approve tools"
                  className={cn(
                    'cursor-pointer',
                    autoApproveTools &&
                      'bg-primary/10! text-primary! rounded-sm!'
                  )}
                >
                  <Icon icon="zap" />
                </Toggle>
              </Tooltip>
              <Tooltip tooltipContent="Plan mode" side="top">
                <Toggle
                  pressed={planMode}
                  onPressedChange={(pressed) => setPlanMode(pressed)}
                  size="sm"
                  variant="ghost"
                  aria-label="Toggle plan mode"
                  className={cn(
                    'cursor-pointer',
                    planMode && 'bg-primary/10! text-primary! rounded-sm!'
                  )}
                >
                  <Icon icon="light-bulb" />
                </Toggle>
              </Tooltip>
            </PromptInputTools>
            <div className="flex items-center gap-1 mr-0.5">
              {isSubmitting ? (
                <PromptInputSubmit
                  aria-label="Stop"
                  size="icon-sm"
                  status="submitted"
                  disabled
                />
              ) : hasInput ? (
                <PromptInputSubmit
                  aria-label="Send message"
                  size="icon-sm"
                  status={undefined}
                />
              ) : (
                <VoiceRecordingButton
                  onTranscriptionComplete={handleTranscriptionComplete}
                />
              )}
            </div>
          </PromptInputFooter>
        </PromptInput>
      </div>
    );
  }
);
